import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plane, 
  User, 
  CreditCard, 
  Phone,
  Mail,
  Calendar,
  Save,
  Clock,
  User2,
  History,
  Loader2,
  MessageCircle,
  Upload,
  Send,
  Trash2,
  FileText
} from 'lucide-react';
import { CheckoutData, CheckoutHistoryItem } from '../interfaces/Checkout';
import { checkoutService, paymentService, PaymentData } from '../services/checkoutService';
import { useAuth } from '../contexts/AuthContext';
import { PermissionAlert } from '../components/PermissionAlert';
import { DefaultLoader } from '../components';
import MileageEmissionSection from '../components/MileageEmissionSection';
import { formatFlightTime } from '../utils/flightDateTime';

const EmissionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [checkout, setCheckout] = useState<CheckoutData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [history, setHistory] = useState<CheckoutHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [localizador, setLocalizador] = useState('');
  const [emissionLeg, setEmissionLeg] = useState<'full' | 'outbound' | 'inbound'>('full');
  const [partnerName, setPartnerName] = useState('');
  const [partnerPaymentMethod, setPartnerPaymentMethod] = useState('');
  const [costPerAdult, setCostPerAdult] = useState('');
  const [costPerChild, setCostPerChild] = useState('');
  const [boardingFee, setBoardingFee] = useState('');
  const [totalCost, setTotalCost] = useState('');
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentDetails, setPaymentDetails] = useState<PaymentData[]>([]);
  const [whatsappEvents, setWhatsappEvents] = useState<any>(null);
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(false);
  const [boardingPasses, setBoardingPasses] = useState<any[]>([]);
  const [loadingBoardingPasses, setLoadingBoardingPasses] = useState(false);
  const [uploadingBp, setUploadingBp] = useState(false);
  const [sendingBp, setSendingBp] = useState<string | null>(null);


  const statusOptions = [
    { value: 'emission started', label: 'Emissão Iniciada' },
    { value: 'awaiting payment', label: 'Aguardando Pagamento' },
    { value: 'completed', label: 'Completado' },
    { value: 'cancelled', label: 'Cancelado' },
    { value: 'failed', label: 'Falhou' },
  ];

  useEffect(() => {
    if (id) {
      loadCheckoutDetails();
    }
  }, [id]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (checkout && id) {
      loadHistory();
    }
  }, [checkout, id]);

  // Para ida e volta, pré-seleciona o próximo trecho sem localizador.
  useEffect(() => {
    if (!checkout) return;
    if (checkout.tripType !== 'round_trip') {
      setEmissionLeg('full');
      return;
    }
    const emitted = checkout.emissions ?? [];
    const hasOutbound = emitted.some((e) => e.leg === 'outbound' || e.leg === 'full');
    const hasInbound = emitted.some((e) => e.leg === 'inbound' || e.leg === 'full');
    setEmissionLeg(hasOutbound && !hasInbound ? 'inbound' : 'outbound');
  }, [checkout]);

  const loadCheckoutDetails = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError('');
      const data = await checkoutService.getCheckoutById(id);
      setCheckout(data);
      setNewStatus(data.status);
      
      // Buscar detalhes de pagamento do Mercado Pago
      try {
        const payments = await paymentService.getPaymentsByCheckout(id);
        setPaymentDetails(payments);
      } catch {}

      // Buscar eventos WhatsApp
      try {
        setLoadingWhatsapp(true);
        const events = await checkoutService.getWhatsappEvents(id);
        setWhatsappEvents(events);
      } catch {} finally {
        setLoadingWhatsapp(false);
      }

      // Buscar boarding passes
      try {
        setLoadingBoardingPasses(true);
        const bps = await checkoutService.getBoardingPasses(id);
        setBoardingPasses(bps);
      } catch {} finally {
        setLoadingBoardingPasses(false);
      }
      
    } catch (err: any) {
      console.error('Erro ao carregar detalhes do checkout:', err);
      const message = err.response?.data?.message || err.message || 'Erro ao carregar detalhes da passagem';
      setError(message);
      setErrorMessage(message);
      setShowErrorAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    if (!id) return;
    
    try {
      setLoadingHistory(true);
      const historyData = await checkoutService.getCheckoutHistory(id);
      
      if (historyData.length === 0 && checkout) {
        const simulatedHistory: CheckoutHistoryItem[] = [
          {
            id: '1',
            checkoutId: id,
            action: 'status_change',
            status: 'emission started',
            updatedBy: checkout.user?.username || 'Sistema',
            updatedAt: checkout.createdAt || checkout.created_at,
            createdAt: checkout.createdAt || checkout.created_at,
            userInfo: checkout.user
          }
        ];
        
        if (checkout.status !== 'emission started') {
          simulatedHistory.push({
            id: '2',
            checkoutId: id,
            action: 'status_change',
            status: checkout.status,
            previousStatus: 'emission started',
            updatedBy: 'Sistema',
            updatedAt: checkout.updatedAt,
            createdAt: checkout.updatedAt,
          });
        }
        
        setHistory(simulatedHistory);
      } else {
        setHistory(historyData);
      }
    } catch (err: any) {
      console.warn('Não foi possível carregar histórico:', err);
      if (checkout) {
        const basicHistory: CheckoutHistoryItem[] = [
          {
            id: '1',
            checkoutId: id,
            action: 'status_change',
            status: checkout.status,
            updatedBy: checkout.user?.username || 'Sistema',
            updatedAt: checkout.updatedAt,
            createdAt: checkout.updatedAt,
            userInfo: checkout.user
          }
        ];
        setHistory(basicHistory);
      }
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!checkout || !newStatus || newStatus === checkout.status) return;

    try {
      setUpdating(true);
      await checkoutService.updateCheckoutStatus(checkout.id, newStatus, user?.username || 'Sistema');
      
      setCheckout({ ...checkout, status: newStatus as any });
      
      loadHistory();
    } catch (err: any) {
      console.error('Erro ao atualizar status:', err);
      const message = err.response?.data?.message || err.message || 'Erro ao atualizar status';
      setErrorMessage(message);
      setShowErrorAlert(true);
    } finally {
      setUpdating(false);
    }
  };

  const handlePaymentStatusUpdate = async (paymentStatus: 'processando' | 'aprovado' | 'reprovado' | 'reembolsado') => {
    if (!checkout || paymentStatus === checkout.paymentStatus) return;

    try {
      setUpdating(true);
      await checkoutService.updatePaymentStatus(checkout.id, paymentStatus, user?.username || 'Sistema');
      
      setCheckout({ ...checkout, paymentStatus });
      
      loadHistory();
    } catch (err: any) {
      console.error('Erro ao atualizar status do pagamento:', err);
      const message = err.response?.data?.message || err.message || 'Erro ao atualizar status do pagamento';
      setErrorMessage(message);
      setShowErrorAlert(true);
    } finally {
      setUpdating(false);
    }
  };

  const handleAddLocalizador = async () => {
    if (!checkout) return;

    try {
      setUpdating(true);
      const updatedCheckout = await checkoutService.addLocalizador(checkout.id, {
        updatedBy: user?.username || 'Sistema',
        leg: checkout.tripType === 'round_trip' ? emissionLeg : 'full',
        ...(localizador.trim() && { localizador: localizador.trim() }),
        ...(partnerName.trim() && { partnerName: partnerName.trim() }),
        ...(partnerPaymentMethod.trim() && { partnerPaymentMethod: partnerPaymentMethod.trim() }),
        ...(costPerAdult && { costPerAdult: parseFloat(costPerAdult.replace(',', '.')) }),
        ...(costPerChild && { costPerChild: parseFloat(costPerChild.replace(',', '.')) }),
        ...(boardingFee && { boardingFee: parseFloat(boardingFee.replace(',', '.')) }),
        ...(totalCost && { totalCost: parseFloat(totalCost.replace(',', '.')) }),
      });

      setCheckout(updatedCheckout);
      setLocalizador('');
      setEmissionLeg('full');
      setPartnerName('');
      setPartnerPaymentMethod('');
      setCostPerAdult('');
      setCostPerChild('');
      setBoardingFee('');
      setTotalCost('');

      loadHistory();

    } catch (err: any) {
      console.error('Erro ao adicionar localizador:', err);
      const message = err.response?.data?.message || err.message || 'Erro ao adicionar localizador';
      setErrorMessage(message);
      setShowErrorAlert(true);
    } finally {
      setUpdating(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const calculateCorrectTotal = () => {
    if (!checkout) return 0;
    
    let total = checkout.basePrice + checkout.taxes + checkout.baggagePrice + checkout.insurancePrice;
    
    total -= checkout.discount;
    
    if (checkout.paymentMethod === 'pix' && checkout.pixDiscount) {
      total -= checkout.pixDiscount;
    }
    
    return total;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Horário de voo: usa o wall-clock do aeroporto (sem conversão de fuso),
  // para bater com o PDF e exibir corretamente voos internacionais.
  const formatTime = (timeString: string) => formatFlightTime(timeString);

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'emission started': 'badge-pending',
      'awaiting payment': 'badge-processing',
      'completed': 'badge-completed',
      'cancelled': 'badge-cancelled',
      'failed': 'badge-failed',
    };

    const labelMap: { [key: string]: string } = {
      'emission started': 'Emissão Iniciada',
      'awaiting payment': 'Aguardando Pagamento',
      'completed': 'Completado',
      'cancelled': 'Cancelado',
      'failed': 'Falhou',
    };

    return (
      <span className={`badge ${statusMap[status] || 'badge-pending'}`}>
        {labelMap[status] || status}
      </span>
    );
  };

  const getPaymentMethodLabel = (method: string) => {
    const labels: { [key: string]: string } = {
      'credit_card': 'Cartão de Crédito',
      'debit_card': 'Cartão de Débito',
      'pix': 'PIX',
      'kairu': 'Kairu',
      'zartoes': 'Zartões'
    };
    return labels[method] || method;
  };

  const getStatusLabel = (status: string) => {
    const labelMap: { [key: string]: string } = {
      'emission started': 'Emissão Iniciada',
      'awaiting payment': 'Aguardando Pagamento',
      'completed': 'Completado',
      'cancelled': 'Cancelado',
      'failed': 'Falhou',
    };
    return labelMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colorMap: { [key: string]: string } = {
      'emission started': 'var(--color-blue)',
      'awaiting payment': 'var(--color-orange)',
      'completed': 'var(--color-green)',
      'cancelled': 'var(--color-red)',
      'failed': 'var(--color-red)',
    };
    return colorMap[status] || 'var(--color-darkgray)';
  };

  if (loading) {
    return <DefaultLoader title="Detalhes da Emissão" messages={['Carregando detalhes...', 'Processando informações...', 'Quase pronto...']} />;
  }

  if (error || !checkout) {
    return (
      <div>
        <div className="d-flex align-center gap-3 mb-4">
          <button
            onClick={() => navigate('/emissions')}
            className="btn btn-secondary"
          >
            <ArrowLeft size={16} />
            Voltar
          </button>
          <h2>Erro ao carregar passagem</h2>
        </div>
        
        <div className="card">
          <p style={{ color: 'var(--color-red)' }}>
            {error || 'Passagem não encontrada'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {updating && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(2px)'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px 32px',
            borderRadius: 'var(--br-8)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            border: '1px solid var(--color-gainsboro-100)'
          }}>
            <Loader2 size={32} className="animate-spin" style={{ color: 'var(--color-blue)' }} />
            <div style={{
              fontSize: 'var(--font-size-16)',
              fontWeight: 500,
              color: 'var(--color-blue)'
            }}>
              Atualizando status da emissão...
            </div>
            <div style={{
              fontSize: 'var(--font-size-14)',
              color: 'var(--color-darkgray)',
              textAlign: 'center'
            }}>
              Aguarde enquanto processamos a alteração
            </div>
          </div>
        </div>
      )}
      
      <div className="d-flex justify-between align-center mb-4">
        <div className="d-flex align-center gap-3">
          <button
            onClick={() => navigate('/emissions')}
            className="btn btn-secondary"
          >
            <ArrowLeft size={16} />
            Voltar
          </button>
          <div>
            <h2 style={{ 
              fontSize: 'var(--font-size-24)', 
              fontWeight: 600, 
              margin: '0 0 8px 0' 
            }}>
              Detalhes da Passagem
            </h2>
            <p style={{ 
              fontSize: 'var(--font-size-16)', 
              color: 'var(--color-darkgray)', 
              margin: 0 
            }}>
              ID: {checkout.id}
            </p>
          </div>
        </div>

        <div className="d-flex align-center gap-3">
          {(checkout.status === 'emission started' || updating) && (
            <div className="d-flex flex-column align-center" style={{
              padding: '12px 16px',
              backgroundColor: updating ? 'var(--color-orange)' : 'var(--color-blue)',
              color: 'white',
              borderRadius: 'var(--br-5)',
              fontSize: 'var(--font-size-12)',
              fontWeight: 500,
              textAlign: 'center',
              minWidth: '180px'
            }}>
              <div style={{ marginBottom: '4px', fontSize: 'var(--font-size-11)', opacity: 0.9 }}>
                Iniciado por: {user?.username || 'N/A'}
              </div>
              <div style={{ marginBottom: '6px', fontSize: 'var(--font-size-11)', opacity: 0.9 }}>
                {currentTime.toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })}
              </div>
              <span>{updating ? 'Atualizando às' : 'Emissão iniciada às'}</span>
              <span style={{ fontWeight: 600 }}>
                {currentTime.toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </span>
            </div>
          )}
          {getStatusBadge(checkout.status)}
        </div>
      </div>

      <div className="grid grid-responsive gap-4" style={{ 
        gridTemplateColumns: 'minmax(0, 1fr) minmax(300px, 350px)', 
        gap: '24px'
      }}>
        <div className="d-flex flex-column gap-4">
          {/* Informações do Passageiro */}
          <div className="card">
            <h3 className="d-flex align-center gap-2 mb-3" style={{ margin: 0, fontSize: 'var(--font-size-18)', fontWeight: 600 }}>
              <User size={20} />
              Dados do Passageiro
            </h3>
            
            {checkout.passengers && checkout.passengers.length > 0 ? (
              <div className="d-flex flex-column gap-3">
                {checkout.passengers.map((passenger, index) => (
                  <div key={passenger.id} className="card" style={{ 
                    backgroundColor: '#f8f9fa',
                    border: '1px solid var(--color-gainsboro-100)',
                    margin: 0
                  }}>
                    {/* Header do passageiro com badge de bagagem */}
                    <div className="d-flex align-center justify-between" style={{ marginBottom: '12px' }}>
                      <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', fontWeight: 500 }}>
                        Passageiro {index + 1}
                      </span>
                      {passenger.hasBaggage ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          backgroundColor: '#dbeafe',
                          color: '#1d4ed8',
                          fontSize: 'var(--font-size-12)',
                          fontWeight: 600,
                          padding: '2px 10px',
                          borderRadius: '999px',
                          border: '1px solid #bfdbfe'
                        }}>
                          🧳 Bagagem despachada (23kg)
                        </span>
                      ) : (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          backgroundColor: '#f3f4f6',
                          color: '#6b7280',
                          fontSize: 'var(--font-size-12)',
                          fontWeight: 500,
                          padding: '2px 10px',
                          borderRadius: '999px',
                          border: '1px solid #e5e7eb'
                        }}>
                          Sem bagagem despachada
                        </span>
                      )}
                    </div>

                    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
                      <div>
                        <label className="form-label" style={{ marginBottom: '4px', fontSize: 'var(--font-size-12)' }}>
                          Nome Completo
                        </label>
                        <p style={{ margin: 0, fontWeight: 500, fontSize: 'var(--font-size-14)' }}>
                          {passenger.firstName} {passenger.lastName}
                        </p>
                      </div>
                      
                      <div>
                        <label className="form-label" style={{ marginBottom: '4px', fontSize: 'var(--font-size-12)' }}>
                          Documento
                        </label>
                        <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>
                          {passenger.documentType}: {passenger.documentNumber}
                        </p>
                      </div>
                      
                      <div>
                        <label className="form-label" style={{ marginBottom: '4px', fontSize: 'var(--font-size-12)' }}>
                          Data de Nascimento
                        </label>
                        <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>
                          {passenger.birthDay.toString().padStart(2, '0')}/{passenger.birthMonth.toString().padStart(2, '0')}/{passenger.birthYear}
                        </p>
                      </div>
                      
                      <div>
                        <label className="form-label" style={{ marginBottom: '4px', fontSize: 'var(--font-size-12)' }}>
                          Gênero
                        </label>
                        <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>
                          {(passenger.gender && (passenger.gender.toLowerCase() === 'male' || passenger.gender === 'MALE')) ? 'Masculino' : 'Feminino'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center" style={{ padding: '32px', color: 'var(--color-darkgray)' }}>
                <User size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <p style={{ margin: 0, fontSize: 'var(--font-size-16)' }}>
                  Nenhum dado de passageiro disponível
                </p>
              </div>
            )}
          </div>

          <div className="card">
            <h3 className="d-flex align-center gap-2 mb-3" style={{ margin: 0, fontSize: 'var(--font-size-18)', fontWeight: 600 }}>
              <Phone size={20} />
              Contato
            </h3>
            
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
              <div>
                <label className="form-label" style={{ marginBottom: '4px', fontSize: 'var(--font-size-12)' }}>
                  Email
                </label>
                <p className="d-flex align-center gap-2" style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>
                  <Mail size={14} color="var(--color-blue)" />
                  {checkout.email}
                </p>
              </div>
              
              <div>
                <label className="form-label" style={{ marginBottom: '4px', fontSize: 'var(--font-size-12)' }}>
                  Telefone
                </label>
                <p className="d-flex align-center gap-2" style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>
                  <Phone size={14} color="var(--color-green)" />
                  +{checkout.countryCode} ({checkout.areaCode}) {checkout.phoneNumber}
                </p>
              </div>
            </div>
          </div>

          {/* Detalhes dos Voos */}
          <div className="card">
            <h3 className="d-flex align-center gap-2 mb-3" style={{ margin: 0, fontSize: 'var(--font-size-18)', fontWeight: 600 }}>
              <Plane size={20} />
              Detalhes dos Voos
            </h3>

            {checkout.flights && checkout.flights.length > 0 ? (
              <div className="d-flex flex-column gap-4">
                {checkout.flights.map((flight) => {
                  const isOutbound = flight.type === 'OUTBOUND' || flight.type === 'outbound';
                  const accentColor = isOutbound ? '#1e90ff' : '#16a34a';
                  const accentBg = isOutbound ? '#eff6ff' : '#f0fdf4';
                  const accentBorder = isOutbound ? '#bfdbfe' : '#bbf7d0';

                  // Monta os nós da timeline: origem + escalas + destino final
                  const sortedStops = (flight.stops || []).slice().sort((a, b) => a.stopOrder - b.stopOrder);

                  type TimelineNode = {
                    iata: string;
                    fullName: string;
                    time: string;
                    date: string;
                    flightCode?: string;
                    duration?: string;
                    waitTime?: string;
                    isConnection: boolean;
                  };

                  const nodes: TimelineNode[] = [];

                  if (sortedStops.length === 0) {
                    // Voo direto
                    nodes.push({
                      iata: flight.departureLocation?.split(' ')[0] || flight.departureLocation,
                      fullName: flight.departureLocation,
                      time: formatTime(flight.departureTime),
                      date: new Date(flight.departureTime).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }),
                      flightCode: `${flight.airlineName} · ${flight.flightNumber}`,
                      duration: flight.flightDuration,
                      isConnection: false,
                    });
                    nodes.push({
                      iata: flight.arrivalLocation?.split(' ')[0] || flight.arrivalLocation,
                      fullName: flight.arrivalLocation,
                      time: formatTime(flight.arrivalTime),
                      date: new Date(flight.arrivalTime).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }),
                      isConnection: false,
                    });
                  } else {
                    // Com escalas: cada stop é um segmento
                    sortedStops.forEach((stop, i) => {
                      const isFirst = i === 0;
                      nodes.push({
                        iata: stop.departureLocation?.split(' ')[0] || stop.departureLocation,
                        fullName: stop.departureLocation,
                        time: formatTime(stop.departureTime),
                        date: new Date(stop.departureTime).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }),
                        flightCode: `${stop.operantCompany || stop.companyCode || ''} ${stop.flightNumber}`.trim(),
                        duration: stop.flightDuration,
                        waitTime: i > 0 ? stop.timeWaiting : undefined,
                        isConnection: !isFirst,
                      });
                    });
                    // Destino final = arrivalLocation do último stop
                    const lastStop = sortedStops[sortedStops.length - 1];
                    nodes.push({
                      iata: lastStop.arrivalLocation?.split(' ')[0] || lastStop.arrivalLocation,
                      fullName: lastStop.arrivalLocation,
                      time: formatTime(lastStop.arrivalTime),
                      date: new Date(lastStop.arrivalTime).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }),
                      isConnection: false,
                    });
                  }

                  return (
                    <div key={flight.id} style={{
                      border: `1px solid ${accentBorder}`,
                      borderRadius: '12px',
                      overflow: 'hidden',
                      backgroundColor: 'white',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    }}>
                      {/* Header */}
                      <div style={{
                        backgroundColor: accentBg,
                        borderBottom: `1px solid ${accentBorder}`,
                        padding: '12px 20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '8px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          <span style={{
                            backgroundColor: accentColor,
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: 700,
                            letterSpacing: '0.8px',
                            textTransform: 'uppercase',
                          }}>
                            {isOutbound ? '✈ IDA' : '✈ VOLTA'}
                          </span>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: accentColor }}>
                            {flight.airlineName}
                          </span>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: 700,
                            color: 'white',
                            backgroundColor: accentColor,
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontFamily: 'monospace',
                            letterSpacing: '1px',
                          }}>
                            {flight.flightNumber}
                          </span>
                          {flight.cabin && (
                            <span style={{
                              fontSize: '11px',
                              color: accentColor,
                              border: `1px solid ${accentBorder}`,
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontWeight: 500,
                            }}>
                              {flight.cabin}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '13px', color: 'var(--color-darkgray)', fontWeight: 500 }}>
                            ⏱ {flight.flightDuration}
                          </span>
                          <span style={{
                            fontSize: '12px',
                            color: flight.qntStop === 0 ? '#16a34a' : '#ea580c',
                            fontWeight: 600,
                            backgroundColor: flight.qntStop === 0 ? '#f0fdf4' : '#fff7ed',
                            border: `1px solid ${flight.qntStop === 0 ? '#bbf7d0' : '#fed7aa'}`,
                            padding: '3px 10px',
                            borderRadius: '20px',
                          }}>
                            {flight.qntStop === 0 ? 'Direto' : `${flight.qntStop} conexão${flight.qntStop > 1 ? 'ões' : ''}`}
                          </span>
                        </div>
                      </div>

                      {/* Timeline */}
                      <div style={{ padding: '24px 28px' }}>
                        {nodes.map((node, idx) => {
                          const isLast = idx === nodes.length - 1;
                          const hasSegmentBelow = !isLast;

                          return (
                            <div key={idx} style={{ display: 'flex', gap: '16px' }}>
                              {/* Coluna esquerda: dot + linha */}
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '20px', flexShrink: 0 }}>
                                <div style={{
                                  width: '14px',
                                  height: '14px',
                                  borderRadius: '50%',
                                  backgroundColor: node.isConnection ? '#ea580c' : accentColor,
                                  border: '2px solid white',
                                  boxShadow: `0 0 0 2px ${node.isConnection ? '#fed7aa' : accentBorder}`,
                                  flexShrink: 0,
                                  marginTop: '4px',
                                }} />
                                {hasSegmentBelow && (
                                  <div style={{
                                    flex: 1,
                                    width: '2px',
                                    background: `repeating-linear-gradient(to bottom, ${accentBorder} 0px, ${accentBorder} 6px, transparent 6px, transparent 12px)`,
                                    margin: '4px 0',
                                    minHeight: '48px',
                                  }} />
                                )}
                              </div>

                              {/* Coluna direita: conteúdo */}
                              <div style={{ flex: 1, paddingBottom: hasSegmentBelow ? '0' : '0' }}>
                                {/* Nó: aeroporto */}
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  justifyContent: 'space-between',
                                  gap: '12px',
                                  flexWrap: 'wrap',
                                }}>
                                  <div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                                      <span style={{
                                        fontSize: '26px',
                                        fontWeight: 800,
                                        color: '#1a1a2e',
                                        letterSpacing: '1px',
                                        lineHeight: 1,
                                      }}>
                                        {node.iata}
                                      </span>
                                      {node.isConnection && (
                                        <span style={{
                                          fontSize: '10px',
                                          fontWeight: 700,
                                          color: '#ea580c',
                                          backgroundColor: '#fff7ed',
                                          border: '1px solid #fed7aa',
                                          padding: '2px 6px',
                                          borderRadius: '4px',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.5px',
                                        }}>
                                          Conexão
                                        </span>
                                      )}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                      {node.fullName}
                                    </div>
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '22px', fontWeight: 700, color: accentColor, lineHeight: 1 }}>
                                      {node.time}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                      {node.date}
                                    </div>
                                  </div>
                                </div>

                                {/* Segmento abaixo (voo + duração) */}
                                {hasSegmentBelow && (
                                  <div style={{
                                    margin: '10px 0 10px 0',
                                    padding: '8px 14px',
                                    backgroundColor: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '8px',
                                    flexWrap: 'wrap',
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <Plane size={13} style={{ color: accentColor, flexShrink: 0 }} />
                                      {node.flightCode && (
                                        <span style={{
                                          fontSize: '12px',
                                          fontWeight: 600,
                                          color: '#374151',
                                          fontFamily: 'monospace',
                                        }}>
                                          {node.flightCode}
                                        </span>
                                      )}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                      {node.duration && (
                                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                          ⏱ {node.duration}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Tempo de espera na conexão */}
                                {node.isConnection && node.waitTime && (
                                  <div style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '11px',
                                    color: '#ea580c',
                                    backgroundColor: '#fff7ed',
                                    border: '1px solid #fed7aa',
                                    borderRadius: '6px',
                                    padding: '3px 10px',
                                    marginBottom: '8px',
                                    fontWeight: 500,
                                  }}>
                                    <Clock size={11} />
                                    Espera: {node.waitTime}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center" style={{ padding: '48px', color: 'var(--color-darkgray)' }}>
                <Plane size={64} style={{ marginBottom: '16px', opacity: 0.3 }} />
                <h4 style={{ margin: '0 0 8px 0', fontSize: 'var(--font-size-18)', fontWeight: 500 }}>
                  Nenhum detalhe de voo disponível
                </h4>
                <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>
                  As informações do voo não foram encontradas ou ainda não foram carregadas.
                </p>
              </div>
            )}
          </div>


          {/* Cartão de Embarque - só aparece se status completed e tem localizador */}
          {checkout.status === 'completed' && checkout.localizador && (
          <div className="card">
            <h3 className="d-flex align-center gap-2 mb-3" style={{ margin: 0, fontSize: 'var(--font-size-18)', fontWeight: 600 }}>
              <FileText size={20} color="#1e90ff" />
              Cartão de Embarque
            </h3>

            {/* Upload */}
            {checkout.flights && checkout.flights.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.target as HTMLFormElement;
                    const formData = new FormData(form);
                    if (!formData.get('file') || !(formData.get('file') as File).size) return;
                    setUploadingBp(true);
                    try {
                      formData.append('uploadedBy', user?.username || 'admin');
                      await checkoutService.uploadBoardingPass(id!, formData);
                      const bps = await checkoutService.getBoardingPasses(id!);
                      setBoardingPasses(bps);
                      form.reset();
                    } catch (err: any) {
                      alert(err.response?.data?.message || 'Erro no upload');
                    } finally {
                      setUploadingBp(false);
                    }
                  }}
                  style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                >
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'end' }}>
                    <div style={{ flex: 1, minWidth: '150px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Voo</label>
                      <select name="flightId" required style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }}>
                        {checkout.flights.map((f) => (
                          <React.Fragment key={f.id}>
                            <option value={f.id}>{f.type === 'outbound' || f.type === 'OUTBOUND' ? '✈ IDA' : '✈ VOLTA'} - {f.flightNumber} ({f.departureLocation} → {f.arrivalLocation})</option>
                            {f.stops?.map((s: any) => (
                              <option key={s.id} value={f.id} data-stop-id={s.id}>
                                &nbsp;&nbsp;↳ Conexão {s.stopOrder}: {s.flightNumber} ({s.departureLocation} → {s.arrivalLocation})
                              </option>
                            ))}
                          </React.Fragment>
                        ))}
                      </select>
                    </div>
                    <div style={{ minWidth: '140px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Passageiro</label>
                      <select name="passengerName" required style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }}>
                        {checkout.passengers?.map((p: any) => (
                          <option key={p.id} value={`${p.firstName} ${p.lastName}`}>{p.firstName} {p.lastName}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ width: '80px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Assento</label>
                      <input name="seat" placeholder="12A" style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }} />
                    </div>
                    <div style={{ width: '120px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Portão</label>
                      <input name="gate" placeholder="Não definido" style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }} />
                    </div>

                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <input name="file" type="file" accept="application/pdf" required style={{ fontSize: '13px' }} />
                    <button type="submit" disabled={uploadingBp} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', fontSize: '13px' }}>
                      {uploadingBp ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                      Upload
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Lista de boarding passes */}
            {loadingBoardingPasses ? (
              <div className="d-flex align-center justify-center" style={{ padding: '24px' }}>
                <Loader2 size={16} className="animate-spin" />
              </div>
            ) : boardingPasses.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-13)' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--color-gainsboro-100)', textAlign: 'left' }}>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Passageiro</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Assento</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Portão</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Embarque</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Status</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {boardingPasses.map((bp) => (
                    <tr key={bp.id} style={{ borderBottom: '1px solid var(--color-gainsboro-100)' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>{bp.passengerName}</td>
                      <td style={{ padding: '10px 12px' }}>{bp.seat || '-'}</td>
                      <td style={{ padding: '10px 12px' }}>{bp.gate || <span style={{ color: '#ea580c', fontSize: '12px' }}>Não definido</span>}</td>
                      <td style={{ padding: '10px 12px' }}>{new Date(bp.departureTime).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                      <td style={{ padding: '10px 12px' }}>
                        {bp.sentAt ? (
                          <span style={{ color: '#16a34a', fontSize: '12px', fontWeight: 600, backgroundColor: '#f0fdf4', padding: '2px 8px', borderRadius: '4px' }}>
                            ✅ Enviado {new Date(bp.sentAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                          </span>
                        ) : (
                          <span style={{ color: '#ea580c', fontSize: '12px', fontWeight: 600, backgroundColor: '#fff7ed', padding: '2px 8px', borderRadius: '4px' }}>
                            Pendente
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {bp.url && (
                            <a href={bp.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1e90ff', fontSize: '12px', textDecoration: 'none', fontWeight: 500 }}>
                              📄 Ver
                            </a>
                          )}
                          {!bp.sentAt && (
                            <button
                              disabled={sendingBp === bp.id}
                              onClick={async () => {
                                setSendingBp(bp.id);
                                try {
                                  await checkoutService.sendBoardingPass(id!, bp.id);
                                  const bps = await checkoutService.getBoardingPasses(id!);
                                  setBoardingPasses(bps);
                                } catch (err: any) {
                                  alert(err.response?.data?.message || 'Erro ao enviar');
                                } finally {
                                  setSendingBp(null);
                                }
                              }}
                              style={{ background: '#25D366', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                            >
                              {sendingBp === bp.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                              WhatsApp
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              if (!window.confirm('Remover este cartão de embarque?')) return;
                              try {
                                await checkoutService.deleteBoardingPass(id!, bp.id);
                                setBoardingPasses(boardingPasses.filter((b) => b.id !== bp.id));
                              } catch (err: any) {
                                alert(err.response?.data?.message || 'Erro ao remover');
                              }
                            }}
                            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '4px' }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ color: 'var(--color-darkgray)', fontSize: 'var(--font-size-14)', margin: 0 }}>
                Nenhum cartão de embarque anexado ainda.
              </p>
            )}
          </div>
          )}

          {/* WhatsApp */}
          <div className="card">
            <h3 style={{ margin: '0 0 16px 0', fontSize: 'var(--font-size-18)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MessageCircle size={18} color="#25D366" />
              WhatsApp
            </h3>

            {loadingWhatsapp ? (
              <div className="d-flex align-center justify-center" style={{ padding: '24px' }}>
                <Loader2 size={16} className="animate-spin" />
                <span style={{ marginLeft: '8px', fontSize: 'var(--font-size-14)' }}>Carregando...</span>
              </div>
            ) : whatsappEvents?.messages?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {whatsappEvents.messages.map((msg: any, idx: number) => {
                  const statusEvts = msg.events?.filter((e: any) => e.type === 'status') ?? [];
                  const replyEvts = msg.events?.filter((e: any) => e.type === 'message') ?? [];
                  const lastStatusEvt = statusEvts.length > 0 ? statusEvts[statusEvts.length - 1] : null;
                  const lastStatus = lastStatusEvt?.status || (msg.events?.length > 0 ? 'sent' : 'sent');

                  const statusCfg: Record<string, { color: string; bg: string; border: string; label: string; icon: string }> = {
                    sent:      { color: '#6c757d', bg: '#f8f9fa', border: '#dee2e6', label: 'Enviada',   icon: '📤' },
                    delivered: { color: '#28a745', bg: '#e8f5e9', border: '#a3d9a5', label: 'Entregue',  icon: '✅' },
                    read:      { color: '#007bff', bg: '#e3f2fd', border: '#90caf9', label: 'Lida',      icon: '👁️' },
                    failed:    { color: '#dc3545', bg: '#fdecea', border: '#f5c6cb', label: 'Falhou',    icon: '❌' },
                  };
                  const sc = statusCfg[lastStatus] || statusCfg.sent;

                  // Mapeia códigos de erro do WhatsApp Cloud API para descrições legíveis
                  const errorDescriptions: Record<number, string> = {
                    131026: 'Número fora da janela de 24h ou não tem WhatsApp',
                    131047: 'Re-engagement message — fora da janela',
                    131000: 'Erro genérico no envio',
                    131005: 'Permissão negada',
                    131008: 'Parâmetro obrigatório ausente',
                    131009: 'Parâmetro inválido',
                    131021: 'Número inválido ou não registrado',
                    131051: 'Tipo de mensagem não suportado',
                    131052: 'Mídia inválida',
                    131053: 'Erro no upload da mídia',
                    130429: 'Limite de taxa atingido',
                    131042: 'Conta de negócios não elegível',
                  };

                  return (
                    <div key={idx} style={{
                      border: `1px solid ${sc.border}`,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      backgroundColor: sc.bg,
                    }}>
                      {/* Cabeçalho da mensagem */}
                      <div style={{
                        padding: '10px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '8px',
                        borderBottom: `1px solid ${sc.border}`,
                        backgroundColor: 'rgba(255,255,255,0.6)',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{
                            fontWeight: 600,
                            fontSize: 'var(--font-size-13)',
                            color: '#1a202c',
                            fontFamily: 'monospace',
                            background: '#f1f5f9',
                            padding: '2px 7px',
                            borderRadius: '4px',
                            border: '1px solid #e2e8f0',
                          }}>
                            {msg.template}
                          </span>
                          <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                            {msg.phoneNumber}
                          </span>
                          <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                            {new Date(msg.sentAt).toLocaleString('pt-BR')}
                          </span>
                        </div>
                        <span style={{
                          padding: '3px 10px',
                          borderRadius: '12px',
                          fontSize: 'var(--font-size-12)',
                          fontWeight: 600,
                          color: sc.color,
                          backgroundColor: 'white',
                          border: `1px solid ${sc.border}`,
                        }}>
                          {sc.icon} {sc.label}
                        </span>
                      </div>

                      {/* Detalhes do erro (se falhou) */}
                      {lastStatus === 'failed' && lastStatusEvt && (
                        <div style={{
                          padding: '10px 14px',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px',
                          borderBottom: replyEvts.length > 0 ? `1px solid ${sc.border}` : undefined,
                        }}>
                          <span style={{ fontSize: '14px', marginTop: '1px' }}>⚠️</span>
                          <div>
                            <div style={{ fontSize: 'var(--font-size-12)', fontWeight: 600, color: '#dc3545' }}>
                              {lastStatusEvt.errorTitle || 'Erro no envio'}
                              {lastStatusEvt.errorCode && (
                                <span style={{ fontWeight: 400, marginLeft: '6px', color: '#6c757d' }}>
                                  (código {lastStatusEvt.errorCode})
                                </span>
                              )}
                            </div>
                            {lastStatusEvt.errorCode && errorDescriptions[lastStatusEvt.errorCode] && (
                              <div style={{ fontSize: 'var(--font-size-12)', color: '#6c757d', marginTop: '2px' }}>
                                {errorDescriptions[lastStatusEvt.errorCode]}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Timeline de status */}
                      {statusEvts.length > 1 && (
                        <div style={{
                          padding: '8px 14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          flexWrap: 'wrap',
                          borderBottom: replyEvts.length > 0 ? `1px solid ${sc.border}` : undefined,
                          backgroundColor: 'rgba(255,255,255,0.4)',
                        }}>
                          <span style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginRight: '4px' }}>
                            Histórico:
                          </span>
                          {statusEvts.map((evt: any, i: number) => {
                            const c = statusCfg[evt.status] || statusCfg.sent;
                            return (
                              <span key={i} style={{
                                padding: '2px 7px',
                                borderRadius: '10px',
                                fontSize: 'var(--font-size-11)',
                                fontWeight: 500,
                                color: c.color,
                                backgroundColor: 'white',
                                border: `1px solid ${c.border}`,
                              }}
                                title={new Date(Number(evt.timestamp) * 1000).toLocaleString('pt-BR')}
                              >
                                {c.icon} {c.label}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {/* Respostas do cliente */}
                      {replyEvts.length > 0 && (
                        <div style={{ padding: '10px 14px' }}>
                          <div style={{
                            fontSize: 'var(--font-size-12)',
                            fontWeight: 600,
                            color: '#6f42c1',
                            marginBottom: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}>
                            <span>💬</span> {replyEvts.length === 1 ? 'Resposta do cliente' : `${replyEvts.length} respostas do cliente`}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {replyEvts.map((evt: any, i: number) => {
                              const replyText = evt.payload?.entry?.[0]?.changes?.[0]?.value?.messages?.[0]?.text?.body;
                              const replyTs = evt.timestamp
                                ? new Date(Number(evt.timestamp) * 1000).toLocaleString('pt-BR')
                                : evt.createdAt
                                  ? new Date(evt.createdAt).toLocaleString('pt-BR')
                                  : null;
                              return (
                                <div key={i} style={{
                                  background: 'white',
                                  border: '1px solid #e9d8fd',
                                  borderRadius: '6px',
                                  padding: '8px 12px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '4px',
                                }}>
                                  {replyText ? (
                                    <span style={{ fontSize: 'var(--font-size-13)', color: '#1a202c' }}>
                                      "{replyText}"
                                    </span>
                                  ) : (
                                    <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', fontStyle: 'italic' }}>
                                      Mensagem não textual (áudio, imagem, etc.)
                                    </span>
                                  )}
                                  {replyTs && (
                                    <span style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)' }}>
                                      {replyTs}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center" style={{ padding: '24px', color: 'var(--color-darkgray)' }}>
                <MessageCircle size={32} style={{ marginBottom: '12px', opacity: 0.3 }} />
                <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>
                  Nenhuma mensagem WhatsApp enviada
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar com ações e informações adicionais */}
        <div className="d-flex flex-column gap-4">
          {/* Atualização de Status */}
          <div className="card">
            <h3 style={{ margin: '0 0 16px 0', fontSize: 'var(--font-size-18)' }}>
              Atualizar Status
            </h3>
            
            <div className="form-group mb-3">
              <label htmlFor="status-select" style={{ marginBottom: '8px', fontWeight: 500 }}>
                Novo Status
              </label>
              <select
                id="status-select"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="form-select"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleStatusUpdate}
              disabled={updating || newStatus === checkout.status}
              className="btn btn-primary w-full"
              style={{
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {updating && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1
                }}>
                  <Loader2 size={20} className="animate-spin" />
                </div>
              )}
              <div style={{ opacity: updating ? 0.7 : 1 }}>
                {updating ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Atualizando Status...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Atualizar Status
                  </>
                )}
              </div>
            </button>
          </div>

          {/* Atualização de Status de Pagamento */}
          <div className="card">
            <h3 style={{ margin: '0 0 16px 0', fontSize: 'var(--font-size-18)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CreditCard size={18} />
              Atualizar Status do Pagamento
            </h3>
            
            <div className="form-group mb-3">
              <label htmlFor="payment-status-select" style={{ marginBottom: '8px', fontWeight: 500 }}>
                Novo Status de Pagamento
              </label>
              <select
                id="payment-status-select"
                value={checkout.paymentStatus}
                onChange={(e) => handlePaymentStatusUpdate(e.target.value as any)}
                className="form-select"
                disabled={updating}
              >
                <option value="processando">Processando</option>
                <option value="aprovado">Aprovado</option>
                <option value="reprovado">Reprovado</option>
                <option value="reembolsado">Reembolsado</option>
              </select>
            </div>
            
            <div style={{ 
              padding: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: 'var(--br-5)',
              fontSize: 'var(--font-size-12)',
              color: 'var(--color-darkgray)',
              marginBottom: '16px'
            }}>
              <strong>Regras de Validação:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '16px' }}>
                <li>Só é possível mudar de "Aguardando Pagamento" para "Emissão Iniciada" se o pagamento estiver "Aprovado"</li>
                <li>Só é possível cancelar uma emissão se o pagamento foi "Reembolsado" primeiro</li>
              </ul>
            </div>
          </div>

          {/* Adicionar Localizador */}
          {checkout.status !== 'completed' && (
            <div className="card">
              <h3 style={{ margin: '0 0 16px 0', fontSize: 'var(--font-size-18)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Plane size={18} />
                Concluir Emissão
              </h3>

              {checkout.tripType === 'round_trip' && (
                <div style={{
                  padding: '12px 14px',
                  backgroundColor: '#fff7e6',
                  border: '1px solid #ffe0a3',
                  borderRadius: 'var(--br-5)',
                  fontSize: 'var(--font-size-13)',
                  color: '#ad6800',
                  marginBottom: '16px',
                }}>
                  <strong>⚠️ Viagem de ida e volta:</strong> os trechos podem ser
                  emitidos por companhias/parceiros diferentes (ex: ida pela{' '}
                  <strong>Busca Passagens</strong> e volta pela{' '}
                  <strong>One Milhas</strong>). Nesse caso, emita e preencha{' '}
                  <strong>cada trecho separadamente</strong> — selecione o trecho,
                  informe o localizador e o parceiro correspondente, e salve. A
                  emissão só é concluída quando ida e volta estiverem preenchidas.
                </div>
              )}

              {checkout.tripType === 'round_trip' && (
                <>
                  {(checkout.emissions ?? []).length > 0 && (
                    <div style={{
                      padding: '10px 12px',
                      backgroundColor: '#eef5ff',
                      border: '1px solid #cfe0ff',
                      borderRadius: 'var(--br-5)',
                      fontSize: 'var(--font-size-13)',
                      marginBottom: '12px',
                    }}>
                      <strong>Localizadores já adicionados:</strong>
                      <ul style={{ margin: '6px 0 0 0', paddingLeft: '18px' }}>
                        {(checkout.emissions ?? []).map((e) => (
                          <li key={`${e.leg}-${e.localizador}`}>
                            {e.leg === 'outbound' ? 'Ida' : e.leg === 'inbound' ? 'Volta' : 'Viagem toda'}:{' '}
                            <strong>{e.localizador}</strong>
                            {e.partnerName ? ` (${e.partnerName})` : ''}
                          </li>
                        ))}
                      </ul>
                      <div style={{ marginTop: '6px', color: 'var(--color-darkgray)' }}>
                        Adicione o localizador do trecho restante para concluir a emissão.
                      </div>
                    </div>
                  )}

                  <div className="form-group mb-3">
                    <label style={{ marginBottom: '8px', fontWeight: 500 }}>Trecho deste localizador</label>
                    <select
                      value={emissionLeg}
                      onChange={(e) => setEmissionLeg(e.target.value as 'full' | 'outbound' | 'inbound')}
                      className="form-control"
                      disabled={updating}
                    >
                      <option value="outbound">Ida ({checkout.departureLocation} → {checkout.arrivalLocation})</option>
                      <option value="inbound">Volta ({checkout.arrivalLocation} → {checkout.departureLocation})</option>
                      <option value="full">Viagem toda (um localizador para ida e volta)</option>
                    </select>
                    <small style={{ color: 'var(--color-darkgray)', display: 'block', marginTop: '6px' }}>
                      Use Ida/Volta quando as companhias forem diferentes (ex: ida LATAM, volta Azul).
                    </small>
                  </div>
                </>
              )}

              <div className="form-group mb-3">
                <label htmlFor="localizador-input" style={{ marginBottom: '8px', fontWeight: 500 }}>
                  Localizador da Passagem
                </label>
                <input
                  id="localizador-input"
                  type="text"
                  value={localizador}
                  onChange={(e) => setLocalizador(e.target.value.toUpperCase())}
                  className="form-control"
                  placeholder="Ex: ABC123"
                  disabled={updating}
                  style={{ textTransform: 'uppercase' }}
                />
              </div>

              <div className="form-group mb-3">
                <label style={{ marginBottom: '8px', fontWeight: 500 }}>
                  Parceiro / Companhia
                  {checkout.tripType === 'round_trip' && emissionLeg !== 'full' && (
                    <span style={{ color: 'var(--color-darkgray)', fontWeight: 400 }}>
                      {' '}· {emissionLeg === 'outbound' ? 'Ida' : 'Volta'}
                    </span>
                  )}
                </label>
                <select
                  value={partnerName}
                  onChange={(e) => setPartnerName(e.target.value)}
                  className="form-control"
                  disabled={updating}
                >
                  <option value="">Selecione...</option>
                  <option value="Art Viagens">Art Viagens</option>
                  <option value="Busca Passagens">Busca Passagens</option>
                  <option value="One Milhas">One Milhas</option>
                  <option value="Speed Milhas">Speed Milhas</option>
                  <option value="Latam">Latam</option>
                  <option value="Azul">Azul</option>
                  <option value="Gol">Gol</option>
                  <option value="Patria">Patria</option>
                </select>
              </div>

              <div className="form-group mb-3">
                <label style={{ marginBottom: '8px', fontWeight: 500 }}>Forma de Pagamento ao Parceiro</label>
                <select
                  value={partnerPaymentMethod}
                  onChange={(e) => setPartnerPaymentMethod(e.target.value)}
                  className="form-control"
                  disabled={updating}
                >
                  <option value="">Selecione...</option>
                  <option value="pix">Pix</option>
                  <option value="credit_card">Cartão de Crédito</option>
                  <option value="invoice">Fatura</option>
                </select>
              </div>

              {(() => {
                const hasChild = checkout.passengers?.some(p => new Date().getFullYear() - p.birthYear < 12);
                const cols = hasChild ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr';
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: cols, gap: '16px', alignItems: 'end' }} className="mb-3">
                    <div className="form-group">
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: 'var(--font-size-14)' }}>Custo por Adulto (R$)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={costPerAdult}
                        onChange={(e) => { const v = e.target.value; if (/^[\d]*[,.]?[\d]*$/.test(v)) setCostPerAdult(v); }}
                        className="form-control"
                        placeholder="0,00"
                        disabled={updating}
                        style={{ width: '100%' }}
                      />
                    </div>
                    {hasChild && (
                      <div className="form-group">
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: 'var(--font-size-14)' }}>Custo por Criança (R$)</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={costPerChild}
                          onChange={(e) => { const v = e.target.value; if (/^[\d]*[,.]?[\d]*$/.test(v)) setCostPerChild(v); }}
                          className="form-control"
                          placeholder="0,00"
                          disabled={updating}
                          style={{ width: '100%' }}
                        />
                      </div>
                    )}
                    <div className="form-group">
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: 'var(--font-size-14)' }}>Taxa de Embarque (R$)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={boardingFee}
                        onChange={(e) => { const v = e.target.value; if (/^[\d]*[,.]?[\d]*$/.test(v)) setBoardingFee(v); }}
                        className="form-control"
                        placeholder="0,00"
                        disabled={updating}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div className="form-group">
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: 'var(--font-size-14)' }}>Custo Total (R$)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={totalCost}
                        onChange={(e) => { const v = e.target.value; if (/^[\d]*[,.]?[\d]*$/.test(v)) setTotalCost(v); }}
                        className="form-control"
                        placeholder="0,00"
                        disabled={updating}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                );
              })()}

              {(() => {
                const emitted = checkout.emissions ?? [];
                const isRound = checkout.tripType === 'round_trip';
                // Esta submissão conclui a emissão?
                let willComplete = true;
                if (isRound && emissionLeg !== 'full') {
                  const otherLeg = emissionLeg === 'outbound' ? 'inbound' : 'outbound';
                  const otherAlreadyEmitted = emitted.some(
                    (e) => e.leg === otherLeg || e.leg === 'full',
                  );
                  willComplete = otherAlreadyEmitted;
                }
                return (
                  <div style={{
                    padding: '12px',
                    backgroundColor: willComplete ? '#e8f5e8' : '#fff7e6',
                    borderRadius: 'var(--br-5)',
                    fontSize: 'var(--font-size-12)',
                    color: willComplete ? 'var(--color-darkgreen)' : '#ad6800',
                    marginBottom: '16px',
                    border: willComplete ? '1px solid #c3e6c3' : '1px solid #ffe0a3',
                  }}>
                    {willComplete ? (
                      <>
                        <strong>🎯 Ação Automática:</strong><br />
                        • Status será alterado automaticamente para "Concluído"<br />
                        • Email com o PDF da reserva será enviado ao cliente<br />
                        • Localizador será salvo permanentemente
                      </>
                    ) : (
                      <>
                        <strong>⏳ Emissão parcial:</strong><br />
                        • O localizador deste trecho será salvo<br />
                        • A emissão só é concluída (e o email enviado) após adicionar o localizador do outro trecho
                      </>
                    )}
                  </div>
                );
              })()}

              <button
                onClick={handleAddLocalizador}
                disabled={updating}
                className="btn btn-success w-full"
                style={{
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {updating && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1
                  }}>
                    <Loader2 size={20} className="animate-spin" />
                  </div>
                )}
                <div style={{ opacity: updating ? 0.7 : 1 }}>
                  {updating ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Processando...
                    </>
                  ) : (
                    <>
                      <Plane size={16} />
                      {checkout.tripType === 'round_trip' && emissionLeg !== 'full'
                        ? 'Salvar localizador do trecho'
                        : 'Concluir Emissão e Enviar Email'}
                    </>
                  )}
                </div>
              </button>
            </div>
          )}

          {/* Emissão por Milhas */}
          <MileageEmissionSection
            checkoutId={checkout.id}
            isRoundTrip={checkout.tripType === 'round_trip'}
            airline={checkout.airline}
          />

          {/* Mostrar Localizador(es) se já existir */}
          {(checkout.localizador || (checkout.emissions ?? []).length > 0) && (
            <div className="card" style={{ border: '2px solid var(--color-green)' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: 'var(--font-size-18)', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-green)' }}>
                ✅ Emissão Concluída
              </h3>

              {(() => {
                const multi = (checkout.emissions ?? []).filter((e) => e.leg !== 'full');
                // Quando há localizadores por trecho (ida/volta separados), lista todos.
                if (multi.length > 1) {
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {multi.map((e) => (
                        <div
                          key={`${e.leg}-${e.localizador}`}
                          style={{ padding: '16px', backgroundColor: '#f0f8f0', borderRadius: 'var(--br-5)', textAlign: 'center' }}
                        >
                          <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', marginBottom: '8px' }}>
                            {e.leg === 'outbound' ? 'LOCALIZADOR · IDA' : 'LOCALIZADOR · VOLTA'}
                            {e.partnerName ? ` · ${e.partnerName}` : ''}
                          </div>
                          <div style={{ fontSize: 'var(--font-size-24)', fontWeight: 'bold', color: 'var(--color-green)', fontFamily: 'monospace', letterSpacing: '2px' }}>
                            {e.localizador}
                          </div>
                        </div>
                      ))}
                      <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', textAlign: 'center' }}>
                        Email com o PDF da reserva enviado para: <strong>{checkout.email}</strong>
                      </div>
                    </div>
                  );
                }
                // Caso padrão: um único localizador.
                const single = checkout.localizador || (checkout.emissions ?? [])[0]?.localizador;
                return (
                  <div style={{ padding: '16px', backgroundColor: '#f0f8f0', borderRadius: 'var(--br-5)', textAlign: 'center' }}>
                    <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', marginBottom: '8px' }}>
                      LOCALIZADOR
                    </div>
                    <div style={{ fontSize: 'var(--font-size-24)', fontWeight: 'bold', color: 'var(--color-green)', fontFamily: 'monospace', letterSpacing: '2px' }}>
                      {single}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', marginTop: '8px' }}>
                      Email enviado para: <strong>{checkout.email}</strong>
                    </div>
                  </div>
                );
              })()}

              {(checkout.partnerName || checkout.partnerPaymentMethod || checkout.totalCost) && (
                <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px', fontSize: 'var(--font-size-14)' }}>
                  {checkout.partnerName && (
                    <div className="d-flex justify-between">
                      <span style={{ color: 'var(--color-darkgray)' }}>Parceiro:</span>
                      <strong>{checkout.partnerName}</strong>
                    </div>
                  )}
                  {checkout.partnerPaymentMethod && (
                    <div className="d-flex justify-between">
                      <span style={{ color: 'var(--color-darkgray)' }}>Pgto. ao Parceiro:</span>
                      <strong>{checkout.partnerPaymentMethod}</strong>
                    </div>
                  )}
                  {checkout.costPerAdult != null && (
                    <div className="d-flex justify-between">
                      <span style={{ color: 'var(--color-darkgray)' }}>Custo por Adulto:</span>
                      <strong>{formatCurrency(checkout.costPerAdult)}</strong>
                    </div>
                  )}
                  {checkout.costPerChild != null && (
                    <div className="d-flex justify-between">
                      <span style={{ color: 'var(--color-darkgray)' }}>Custo por Criança:</span>
                      <strong>{formatCurrency(checkout.costPerChild)}</strong>
                    </div>
                  )}
                  {checkout.totalCost != null && (
                    <div className="d-flex justify-between">
                      <span style={{ color: 'var(--color-darkgray)' }}>Custo Total:</span>
                      <strong>{formatCurrency(checkout.totalCost)}</strong>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Informações de Pagamento */}
          <div className="card">
            <h3 style={{ margin: '0 0 16px 0', fontSize: 'var(--font-size-18)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CreditCard size={18} />
              Pagamento
            </h3>
            
            <div className="d-flex flex-column gap-2" style={{ fontSize: 'var(--font-size-14)' }}>
              <div className="d-flex justify-between">
                <span>Status do Pagamento:</span>
                <span className={`badge ${
                  checkout.paymentStatus === 'aprovado' ? 'badge-completed' :
                  checkout.paymentStatus === 'processando' ? 'badge-processing' :
                  checkout.paymentStatus === 'reprovado' ? 'badge-failed' :
                  checkout.paymentStatus === 'reembolsado' ? 'badge-cancelled' : 'badge-pending'
                }`} style={{ fontSize: 'var(--font-size-12)', padding: '2px 6px' }}>
                  {checkout.paymentStatus === 'processando' ? 'Processando' :
                   checkout.paymentStatus === 'aprovado' ? 'Aprovado' :
                   checkout.paymentStatus === 'reprovado' ? 'Reprovado' :
                   checkout.paymentStatus === 'reembolsado' ? 'Reembolsado' : checkout.paymentStatus}
                </span>
              </div>

              <div className="d-flex justify-between">
                <span>Método:</span>
                <span style={{ fontWeight: 500 }}>{getPaymentMethodLabel(checkout.paymentMethod)}</span>
              </div>

              {checkout.transactionId && (
                <div className="d-flex justify-between">
                  <span>ID da Transação:</span>
                  <span style={{ fontWeight: 500, fontSize: 'var(--font-size-12)', wordBreak: 'break-all' }}>
                    {checkout.transactionId}
                  </span>
                </div>
              )}
              
              {checkout.installments && (
                <div className="d-flex justify-between">
                  <span>Parcelas:</span>
                  <span style={{ fontWeight: 500 }}>{checkout.installments}x</span>
                </div>
              )}

              {checkout.pixCode && (
                <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                  <div style={{ marginBottom: '8px', fontWeight: 600, fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                    Código PIX:
                  </div>
                  <div style={{ 
                    fontSize: 'var(--font-size-11)', 
                    wordBreak: 'break-all', 
                    fontFamily: 'monospace',
                    padding: '8px',
                    backgroundColor: 'white',
                    border: '1px solid var(--color-gainsboro-100)',
                    borderRadius: '4px'
                  }}>
                    {checkout.pixCode}
                  </div>
                  
                  {checkout.paymentMethod === 'pix' && !checkout.pixDiscount && (
                    <div style={{ 
                      marginTop: '8px', 
                      padding: '8px', 
                      backgroundColor: '#fff3cd', 
                      border: '1px solid #ffeeba',
                      borderRadius: '4px',
                      fontSize: 'var(--font-size-12)',
                      color: '#856404'
                    }}>
                      ⚠️ <strong>Atenção:</strong> Pagamento PIX sem desconto aplicado. Verifique se o desconto foi processado corretamente.
                    </div>
                  )}
                </div>
              )}

              <hr style={{ margin: '8px 0' }} />
              
              <div className="d-flex justify-between">
                <span>Valor Base:</span>
                <span>{formatCurrency(checkout.basePrice)}</span>
              </div>
              
              <div className="d-flex justify-between">
                <span>Taxas:</span>
                <span>{formatCurrency(checkout.taxes)}</span>
              </div>
              
              {/* Bagagem inclusa na tarifa */}
              {(checkout.outboundHasCheckedBags || checkout.returnHasCheckedBags) && (
                <div className="d-flex justify-between" style={{ color: 'var(--color-green)' }}>
                  <span>
                    Bagagem inclusa na tarifa
                    {checkout.tripType === 'round_trip' && (
                      <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', marginLeft: '6px' }}>
                        (
                        {checkout.outboundHasCheckedBags && checkout.returnHasCheckedBags
                          ? 'ida e volta'
                          : checkout.outboundHasCheckedBags
                          ? 'apenas ida'
                          : 'apenas volta'}
                        )
                      </span>
                    )}
                  </span>
                  <span>✓</span>
                </div>
              )}

              {/* Bagagem adicionada pelo usuário */}
              {checkout.baggagePrice > 0 && (
                <div>
                  <div className="d-flex justify-between">
                    <span>Bagagem adicionada:</span>
                    <span>{formatCurrency(checkout.baggagePrice)}</span>
                  </div>
                  {/* Detalhe por passageiro — quem leva mala */}
                  {checkout.passengers.some(p => p.hasBaggage) && (
                    <div style={{ marginTop: '4px', paddingLeft: '12px', borderLeft: '2px solid var(--color-gainsboro)' }}>
                      {checkout.passengers
                        .filter(p => p.hasBaggage)
                        .map(p => (
                          <div key={p.id} style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-dimgray)', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                            <span>🧳</span>
                            <span>{p.firstName} {p.lastName}</span>
                          </div>
                        ))
                      }
                    </div>
                  )}
                </div>
              )}
              
              {checkout.insurancePrice > 0 && (
                <div className="d-flex justify-between">
                  <span>Seguro:</span>
                  <span>{formatCurrency(checkout.insurancePrice)}</span>
                </div>
              )}
              
              {checkout.discount > 0 && (
                <div className="d-flex justify-between" style={{ color: 'var(--color-green)' }}>
                  <span>Desconto:</span>
                  <span>-{formatCurrency(checkout.discount)}</span>
                </div>
              )}

              {checkout.paymentMethod === 'pix' && (
                <>
                  {checkout.pixDiscount && checkout.pixDiscount > 0 ? (
                    <div className="d-flex justify-between" style={{ color: 'var(--color-green)' }}>
                      <span>Desconto PIX:</span>
                      <span>-{formatCurrency(checkout.pixDiscount)}</span>
                    </div>
                  ) : (
                    <div className="d-flex justify-between" style={{ color: 'var(--color-orange)' }}>
                      <span>Desconto PIX:</span>
                      <span style={{ fontSize: 'var(--font-size-12)', fontStyle: 'italic' }}>Não aplicado</span>
                    </div>
                  )}
                </>
              )}

              {checkout.cashback && checkout.cashback > 0 && (
                <div className="d-flex justify-between" style={{ color: 'var(--color-blue)' }}>
                  <span>Cashback:</span>
                  <span>+{formatCurrency(checkout.cashback)}</span>
                </div>
              )}

              <hr style={{ margin: '8px 0' }} />
              
              <div className="d-flex justify-between" style={{ fontWeight: 600, fontSize: 'var(--font-size-16)' }}>
                <span>Total:</span>
                <div className="d-flex flex-column align-end">
                  <span>{formatCurrency(checkout.totalPrice)}</span>
                  
                  {checkout.paymentMethod === 'pix' && checkout.totalWithPixDiscount && checkout.totalWithPixDiscount !== checkout.totalPrice && (
                    <div style={{ 
                      fontSize: 'var(--font-size-14)', 
                      color: 'var(--color-green)', 
                      fontWeight: 600,
                      marginTop: '4px'
                    }}>
                      Total c/ PIX: {formatCurrency(checkout.totalWithPixDiscount)}
                    </div>
                  )}
                  
                  {checkout.paymentMethod === 'pix' && Math.abs(calculateCorrectTotal() - checkout.totalPrice) > 0.01 && (
                    <div style={{ 
                      fontSize: 'var(--font-size-12)', 
                      color: 'var(--color-orange)', 
                      fontWeight: 400,
                      fontStyle: 'italic',
                      marginTop: '2px'
                    }}>
                      Recalculado: {formatCurrency(calculateCorrectTotal())}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tentativas de Pagamento Mercado Pago */}
          {paymentDetails.length > 0 && (() => {
            const mpStatusLabel: Record<string, string> = {
              approved: 'Aprovado',
              pending: 'Pendente',
              in_process: 'Em análise',
              rejected: 'Reprovado',
              cancelled: 'Cancelado',
              refunded: 'Reembolsado',
              charged_back: 'Estornado',
            };
            const mpStatusColor: Record<string, { bg: string; border: string; text: string }> = {
              approved:     { bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d' },
              pending:      { bg: '#fffbeb', border: '#fde68a', text: '#92400e' },
              in_process:   { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' },
              rejected:     { bg: '#fef2f2', border: '#fecaca', text: '#dc2626' },
              cancelled:    { bg: '#f3f4f6', border: '#d1d5db', text: '#6b7280' },
              refunded:     { bg: '#f5f3ff', border: '#ddd6fe', text: '#7c3aed' },
              charged_back: { bg: '#fff7ed', border: '#fed7aa', text: '#c2410c' },
            };
            const mpStatusDetailMessages: Record<string, string> = {
              accredited: 'Pagamento acreditado com sucesso',
              pending_contingency: 'Pagamento pendente — aguardando processamento',
              pending_review_manual: 'Em revisão manual pelo Mercado Pago',
              cc_rejected_bad_filled_card_number: 'Número do cartão inválido',
              cc_rejected_bad_filled_date: 'Data de validade inválida',
              cc_rejected_bad_filled_other: 'Dados do cartão incorretos',
              cc_rejected_bad_filled_security_code: 'Código de segurança inválido',
              cc_rejected_blacklist: 'Cartão bloqueado / lista negra',
              cc_rejected_call_for_authorize: 'Banco solicitou autorização — cliente deve ligar',
              cc_rejected_card_disabled: 'Cartão desabilitado pelo banco',
              cc_rejected_card_error: 'Erro no processamento do cartão',
              cc_rejected_duplicated_payment: 'Pagamento duplicado detectado',
              cc_rejected_high_risk: 'Recusado por análise de risco (antifraude)',
              cc_rejected_insufficient_amount: 'Saldo insuficiente',
              cc_rejected_invalid_installments: 'Número de parcelas inválido',
              cc_rejected_max_attempts: 'Limite de tentativas excedido',
              cc_rejected_other_reason: 'Cartão recusado (motivo genérico)',
              FUND: 'Saldo insuficiente',
              SECU: 'Código de segurança inválido',
              EXPI: 'Data de validade expirada',
              FORM: 'Erro de formulário',
              CALL: 'Ligar para o banco para autorizar',
              OTHE: 'Recusado — erro geral',
            };
            const cardBrandLabel: Record<string, string> = {
              visa: 'Visa', master: 'Mastercard', amex: 'American Express',
              elo: 'Elo', hipercard: 'Hipercard', hiper: 'Hiper',
              diners: 'Diners Club', discover: 'Discover',
            };

            return (
              <div className="card">
                <h3 style={{ margin: '0 0 16px 0', fontSize: 'var(--font-size-18)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CreditCard size={18} />
                  Tentativas de Pagamento
                  <span style={{
                    marginLeft: '4px', fontSize: 'var(--font-size-12)', fontWeight: 400,
                    backgroundColor: '#f3f4f6', border: '1px solid #d1d5db',
                    borderRadius: '12px', padding: '1px 8px', color: '#6b7280'
                  }}>
                    {paymentDetails.length}
                  </span>
                </h3>

                <div className="d-flex flex-column" style={{ gap: '16px' }}>
                  {paymentDetails.map((payment, idx) => {
                    const mpData = payment.mercadoPagoResponse || payment.webhookData;
                    const statusDetail = payment.webhookData?.status_detail || payment.mercadoPagoResponse?.status_detail;
                    const mpStatus = payment.webhookData?.status || payment.mercadoPagoResponse?.status || payment.status;
                    const colors = mpStatusColor[mpStatus] || mpStatusColor['cancelled'];
                    const transactionDetails = mpData?.transaction_details;
                    const chargesDetails = mpData?.charges_details || [];
                    const additionalPayer = mpData?.additional_info?.payer;
                    const additionalAddress = additionalPayer?.address;
                    const threeDsInfo = mpData?.three_ds_info;
                    const has3ds = !!(threeDsInfo?.creq || threeDsInfo?.external_resource_url);
                    const mpFee = chargesDetails.find((c: any) => c.name === 'mercadopago_fee');
                    const statementDescriptor = mpData?.statement_descriptor;
                    const moneyReleaseDate = payment.webhookData?.money_release_date || payment.mercadoPagoResponse?.money_release_date;
                    const moneyReleaseStatus = payment.webhookData?.money_release_status || payment.mercadoPagoResponse?.money_release_status;
                    const processingMode = mpData?.processing_mode;
                    const operationType = mpData?.operation_type;

                    return (
                      <div key={payment.id} style={{
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        backgroundColor: colors.bg,
                        overflow: 'hidden',
                      }}>
                        {/* Cabeçalho da tentativa */}
                        <div style={{
                          padding: '12px 16px',
                          backgroundColor: colors.bg,
                          borderBottom: `1px solid ${colors.border}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: '8px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{
                              fontSize: 'var(--font-size-12)', fontWeight: 700,
                              color: 'var(--color-darkgray)',
                            }}>
                              #{idx + 1}
                            </span>
                            <span style={{
                              fontSize: 'var(--font-size-12)', fontWeight: 700,
                              backgroundColor: colors.border, color: colors.text,
                              padding: '2px 10px', borderRadius: '20px',
                            }}>
                              {mpStatusLabel[mpStatus] || mpStatus}
                            </span>
                            {statusDetail && (
                              <span style={{
                                fontSize: 'var(--font-size-11)', color: colors.text,
                                backgroundColor: 'white', border: `1px solid ${colors.border}`,
                                padding: '2px 8px', borderRadius: '20px',
                              }}>
                                {mpStatusDetailMessages[statusDetail] || statusDetail}
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-dimgray)' }}>
                            {new Date(payment.createdAt).toLocaleString('pt-BR')}
                          </span>
                        </div>

                        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

                          {/* Alerta 3DS */}
                          {has3ds && (
                            <div style={{
                              padding: '10px 12px',
                              backgroundColor: '#eff6ff',
                              border: '1px solid #bfdbfe',
                              borderRadius: '6px',
                              fontSize: 'var(--font-size-12)',
                              color: '#1e40af',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '8px',
                            }}>
                              <span style={{ fontSize: '16px', lineHeight: 1 }}>🔐</span>
                              <div>
                                <strong>Autenticação 3D Secure (3DS) solicitada</strong>
                                <div style={{ marginTop: '2px', color: '#2563eb' }}>
                                  O banco emissor do cartão exigiu autenticação adicional do portador.
                                  {threeDsInfo?.external_resource_url && (
                                    <span> URL de redirecionamento: <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{threeDsInfo.external_resource_url}</span></span>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* IDs e referências */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 12px' }}>
                              <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>ID Mercado Pago</div>
                              <div style={{ fontSize: 'var(--font-size-12)', fontFamily: 'monospace', wordBreak: 'break-all', fontWeight: 500 }}>{payment.mercadoPagoId}</div>
                            </div>
                            {payment.externalReference && (
                              <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 12px' }}>
                                <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Referência Externa</div>
                                <div style={{ fontSize: 'var(--font-size-12)', fontFamily: 'monospace', wordBreak: 'break-all' }}>{payment.externalReference}</div>
                              </div>
                            )}
                          </div>

                          {/* Método de pagamento e cartão */}
                          {(payment.cardBrand || payment.cardLastFourDigits || payment.paymentTypeId) && (
                            <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 14px' }}>
                              <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Cartão</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                {payment.cardBrand && (
                                  <span style={{
                                    fontSize: 'var(--font-size-13)', fontWeight: 700,
                                    backgroundColor: '#f3f4f6', border: '1px solid #d1d5db',
                                    padding: '3px 10px', borderRadius: '6px', color: '#374151'
                                  }}>
                                    {cardBrandLabel[payment.cardBrand] || payment.cardBrand.toUpperCase()}
                                  </span>
                                )}
                                {payment.paymentTypeId === 'credit_card' && (
                                  <span style={{ fontSize: 'var(--font-size-12)', color: '#6b7280' }}>Crédito</span>
                                )}
                                {payment.paymentTypeId === 'debit_card' && (
                                  <span style={{ fontSize: 'var(--font-size-12)', color: '#6b7280' }}>Débito</span>
                                )}
                                {payment.cardLastFourDigits && (
                                  <span style={{ fontSize: 'var(--font-size-14)', fontWeight: 600, fontFamily: 'monospace', letterSpacing: '2px' }}>
                                    •••• •••• •••• {payment.cardLastFourDigits}
                                  </span>
                                )}
                                {(payment.installments ?? 1) > 1 && (
                                  <span style={{ fontSize: 'var(--font-size-12)', color: '#2563eb', fontWeight: 500 }}>
                                    {payment.installments}x de {formatCurrency(Number(payment.amount) / (payment.installments ?? 1))}
                                  </span>
                                )}
                                {(payment.installments ?? 1) === 1 && (
                                  <span style={{ fontSize: 'var(--font-size-12)', color: '#6b7280' }}>À vista</span>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Dados do endereço de cobrança */}
                          {(payment.billingStreetName || payment.billingZipCode) && (
                            <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 14px' }}>
                              <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Endereço de Cobrança</div>
                              <div style={{ fontSize: 'var(--font-size-12)', color: '#374151' }}>
                                {payment.billingStreetName}{payment.billingStreetNumber ? `, ${payment.billingStreetNumber}` : ''}
                                {payment.billingZipCode && (
                                  <span style={{ marginLeft: '8px', color: '#6b7280' }}>CEP: {payment.billingZipCode}</span>
                                )}
                              </div>
                              {additionalAddress && additionalPayer && (
                                <div style={{ marginTop: '4px', fontSize: 'var(--font-size-12)', color: '#6b7280' }}>
                                  Titular: {additionalPayer.first_name} {additionalPayer.last_name}
                                  {additionalPayer.phone && (
                                    <span style={{ marginLeft: '8px' }}>({additionalPayer.phone.area_code}) {additionalPayer.phone.number}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Valores financeiros */}
                          <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 14px' }}>
                            <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Valores</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: 'var(--font-size-13)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#6b7280' }}>Valor cobrado:</span>
                                <strong>{formatCurrency(Number(payment.amount))}</strong>
                              </div>
                              {transactionDetails?.total_paid_amount != null && transactionDetails.total_paid_amount > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ color: '#6b7280' }}>Total pago (MP):</span>
                                  <span>{formatCurrency(transactionDetails.total_paid_amount)}</span>
                                </div>
                              )}
                              {transactionDetails?.net_received_amount != null && transactionDetails.net_received_amount > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ color: '#6b7280' }}>Valor líquido recebido:</span>
                                  <span style={{ color: '#15803d', fontWeight: 500 }}>{formatCurrency(transactionDetails.net_received_amount)}</span>
                                </div>
                              )}
                              {mpFee && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ color: '#6b7280' }}>Taxa Mercado Pago:</span>
                                  <span style={{ color: '#c2410c' }}>-{formatCurrency(mpFee.amounts?.original ?? 0)}</span>
                                </div>
                              )}
                              {(mpData?.transaction_amount_refunded ?? 0) > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ color: '#6b7280' }}>Valor reembolsado:</span>
                                  <span style={{ color: '#7c3aed', fontWeight: 500 }}>{formatCurrency(mpData?.transaction_amount_refunded ?? 0)}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Descriptor de fatura e info de repasse */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            {statementDescriptor && (
                              <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 12px' }}>
                                <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>
                                  Aparece na fatura como
                                </div>
                                <div style={{ fontSize: 'var(--font-size-13)', fontWeight: 700, fontFamily: 'monospace', color: '#111827', letterSpacing: '1px' }}>
                                  {statementDescriptor}
                                </div>
                              </div>
                            )}
                            {(moneyReleaseDate || moneyReleaseStatus) && (
                              <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 12px' }}>
                                <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Repasse</div>
                                {moneyReleaseStatus && (
                                  <div style={{ fontSize: 'var(--font-size-12)', color: moneyReleaseStatus === 'released' ? '#15803d' : '#92400e', fontWeight: 500 }}>
                                    {moneyReleaseStatus === 'released' ? '✓ Liberado' : moneyReleaseStatus === 'pending' ? '⏳ Pendente' : moneyReleaseStatus}
                                  </div>
                                )}
                                {moneyReleaseDate && (
                                  <div style={{ fontSize: 'var(--font-size-12)', color: '#6b7280', marginTop: '2px' }}>
                                    {new Date(moneyReleaseDate).toLocaleDateString('pt-BR')}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Modo de processamento e operação */}
                          {(processingMode || operationType) && (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {processingMode && (
                                <span style={{
                                  fontSize: 'var(--font-size-11)', backgroundColor: '#f3f4f6',
                                  border: '1px solid #d1d5db', borderRadius: '4px',
                                  padding: '3px 8px', color: '#6b7280'
                                }}>
                                  Modo: {processingMode}
                                </span>
                              )}
                              {operationType && (
                                <span style={{
                                  fontSize: 'var(--font-size-11)', backgroundColor: '#f3f4f6',
                                  border: '1px solid #d1d5db', borderRadius: '4px',
                                  padding: '3px 8px', color: '#6b7280'
                                }}>
                                  Operação: {operationType}
                                </span>
                              )}
                              {mpData?.live_mode === true && (
                                <span style={{
                                  fontSize: 'var(--font-size-11)', backgroundColor: '#f0fdf4',
                                  border: '1px solid #bbf7d0', borderRadius: '4px',
                                  padding: '3px 8px', color: '#15803d', fontWeight: 600
                                }}>
                                  PRODUÇÃO
                                </span>
                              )}
                              {mpData?.live_mode === false && (
                                <span style={{
                                  fontSize: 'var(--font-size-11)', backgroundColor: '#fef3c7',
                                  border: '1px solid #fde68a', borderRadius: '4px',
                                  padding: '3px 8px', color: '#92400e', fontWeight: 600
                                }}>
                                  SANDBOX
                                </span>
                              )}
                            </div>
                          )}

                          {/* Datas */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '6px' }}>
                            {mpData?.date_created && (
                              <div style={{ fontSize: 'var(--font-size-12)', color: '#6b7280' }}>
                                <span style={{ fontWeight: 500, color: '#374151' }}>Criado: </span>
                                {new Date(mpData.date_created).toLocaleString('pt-BR')}
                              </div>
                            )}
                            {mpData?.date_approved && (
                              <div style={{ fontSize: 'var(--font-size-12)', color: '#15803d' }}>
                                <span style={{ fontWeight: 500 }}>Aprovado: </span>
                                {new Date(mpData.date_approved).toLocaleString('pt-BR')}
                              </div>
                            )}
                            {payment.paidAt && (
                              <div style={{ fontSize: 'var(--font-size-12)', color: '#15803d' }}>
                                <span style={{ fontWeight: 500 }}>Pago em: </span>
                                {new Date(payment.paidAt).toLocaleString('pt-BR')}
                              </div>
                            )}
                            {payment.expiresAt && (
                              <div style={{ fontSize: 'var(--font-size-12)', color: '#c2410c' }}>
                                <span style={{ fontWeight: 500 }}>Expira: </span>
                                {new Date(payment.expiresAt).toLocaleString('pt-BR')}
                              </div>
                            )}
                          </div>

                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          <div className="card">
            <h3 style={{ margin: '0 0 16px 0', fontSize: 'var(--font-size-18)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Plane size={18} />
              Resumo do Voo
            </h3>
            
            <div className="d-flex flex-column gap-3" style={{ fontSize: 'var(--font-size-14)' }}>
              <div>
                <span style={{ color: 'var(--color-darkgray)', fontSize: 'var(--font-size-12)' }}>Rota:</span>
                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-16)', color: 'var(--color-blue)' }}>
                  {checkout.flightRoute}
                </div>
              </div>

              <div>
                <span style={{ color: 'var(--color-darkgray)', fontSize: 'var(--font-size-12)' }}>Companhia Aérea:</span>
                <div style={{ fontWeight: 500 }}>{checkout.airline}</div>
              </div>

              {/* Origem do voo e família tarifária — relevante para voos Befly */}
              {(checkout.flightSource || checkout.flight_origin) && (
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  backgroundColor: '#f0f7ff',
                  border: '1px solid #bfdbfe',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      fontSize: '10px',
                      fontWeight: 700,
                      letterSpacing: '0.6px',
                      textTransform: 'uppercase',
                      color: '#1d4ed8',
                      backgroundColor: '#dbeafe',
                      border: '1px solid #bfdbfe',
                      padding: '2px 8px',
                      borderRadius: '20px',
                    }}>
                      {(checkout.flightSource || checkout.flight_origin || '').toUpperCase()}
                    </span>
                    <span style={{ fontSize: 'var(--font-size-12)', color: '#1d4ed8', fontWeight: 500 }}>
                      Origem do voo
                    </span>
                  </div>
                  {checkout.fareFamily && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        fontSize: '10px',
                        fontWeight: 700,
                        letterSpacing: '0.6px',
                        textTransform: 'uppercase',
                        color: '#7c3aed',
                        backgroundColor: '#ede9fe',
                        border: '1px solid #ddd6fe',
                        padding: '2px 8px',
                        borderRadius: '20px',
                      }}>
                        {checkout.fareFamily}
                      </span>
                      <span style={{ fontSize: 'var(--font-size-12)', color: '#7c3aed', fontWeight: 500 }}>
                        Tarifa escolhida
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div>
                <span style={{ color: 'var(--color-darkgray)', fontSize: 'var(--font-size-12)' }}>Data do Voo:</span>
                <div style={{ fontWeight: 500 }}>{checkout.flightDate}</div>
              </div>

              <div className="grid gap-2" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div>
                  <span style={{ color: 'var(--color-darkgray)', fontSize: 'var(--font-size-12)' }}>Partida:</span>
                  <div style={{ fontWeight: 500 }}>{checkout.departureTime}</div>
                  <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                    {checkout.departureLocation}
                  </div>
                </div>
                <div>
                  <span style={{ color: 'var(--color-darkgray)', fontSize: 'var(--font-size-12)' }}>Chegada:</span>
                  <div style={{ fontWeight: 500 }}>{checkout.arrivalTime}</div>
                  <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                    {checkout.arrivalLocation}
                  </div>
                </div>
              </div>

              <div>
                <span style={{ color: 'var(--color-darkgray)', fontSize: 'var(--font-size-12)' }}>Duração do Voo:</span>
                <div style={{ fontWeight: 500 }}>{checkout.flightDuration}</div>
              </div>

              {checkout.tripType === 'round_trip' && checkout.returnFlightDate && (
                <>
                  <hr style={{ margin: '8px 0' }} />
                  <div>
                    <span style={{ color: 'var(--color-darkgray)', fontSize: 'var(--font-size-12)' }}>Voo de Volta:</span>
                    <div style={{ fontWeight: 500 }}>{checkout.returnFlightDate}</div>
                  </div>
                  
                  <div className="grid gap-2" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div>
                      <span style={{ color: 'var(--color-darkgray)', fontSize: 'var(--font-size-12)' }}>Partida:</span>
                      <div style={{ fontWeight: 500 }}>{checkout.returnDepartureTime}</div>
                    </div>
                    <div>
                      <span style={{ color: 'var(--color-darkgray)', fontSize: 'var(--font-size-12)' }}>Chegada:</span>
                      <div style={{ fontWeight: 500 }}>{checkout.returnArrivalTime}</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Informações Adicionais */}
          <div className="card">
            <h3 style={{ margin: '0 0 16px 0', fontSize: 'var(--font-size-18)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={18} />
              Informações
            </h3>
            
            <div className="d-flex flex-column gap-2" style={{ fontSize: 'var(--font-size-14)' }}>
              <div>
                <span style={{ color: 'var(--color-darkgray)' }}>Criado em:</span>
                <div style={{ fontWeight: 500 }}>{formatDate(checkout.createdAt || checkout.created_at)}</div>
              </div>
              
              <div>
                <span style={{ color: 'var(--color-darkgray)' }}>Atualizado em:</span>
                <div style={{ fontWeight: 500 }}>{formatDate(checkout.updatedAt)}</div>
              </div>

              {checkout.completedAt && (
                <div>
                  <span style={{ color: 'var(--color-darkgray)' }}>Completado em:</span>
                  <div style={{ fontWeight: 500 }}>{formatDate(checkout.completedAt)}</div>
                </div>
              )}
              
              <div>
                <span style={{ color: 'var(--color-darkgray)' }}>Tipo de viagem:</span>
                <div style={{ fontWeight: 500 }}>
                  {checkout.tripType === 'one_way' ? 'Somente ida' : 'Ida e volta'}
                </div>
              </div>

              {checkout.user && (
                <>
                  <hr style={{ margin: '8px 0' }} />
                  <div>
                    <span style={{ color: 'var(--color-darkgray)' }}>Usuário:</span>
                    <div style={{ fontWeight: 500 }}>
                      {checkout.user.username} ({checkout.user.email})
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Histórico de Alterações */}
          <div className="card">
            <h3 style={{ margin: '0 0 16px 0', fontSize: 'var(--font-size-18)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <History size={18} />
              Histórico de Alterações
            </h3>
            
            {loadingHistory ? (
              <div className="d-flex align-center justify-center" style={{ padding: '24px' }}>
                <Loader2 size={16} className="animate-spin" />
                <span style={{ marginLeft: '8px', fontSize: 'var(--font-size-14)' }}>Carregando histórico...</span>
              </div>
            ) : history.length > 0 ? (
              <div className="d-flex flex-column gap-3">
                {history
                  .sort((a, b) => new Date(b.createdAt ?? b.updatedAt ?? 0).getTime() - new Date(a.createdAt ?? a.updatedAt ?? 0).getTime())
                  .map((item, index) => (
                    <div key={item.id} className="d-flex gap-3" style={{ position: 'relative' }}>
                      {/* Timeline line */}
                      {index < history.length - 1 && (
                        <div style={{
                          position: 'absolute',
                          left: '15px',
                          top: '32px',
                          bottom: '-12px',
                          width: '2px',
                          backgroundColor: 'var(--color-gainsboro-100)'
                        }} />
                      )}
                      
                      {/* Status indicator */}
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: getStatusColor(item.status ?? ''),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        zIndex: 1,
                        border: '2px solid white',
                        boxShadow: '0 0 0 2px var(--color-gainsboro-100)'
                      }}>
                        <Clock size={14} color="white" />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1" style={{ minWidth: 0 }}>
                        <div className="d-flex justify-between align-center mb-1">
                          <span style={{ 
                            fontWeight: 600, 
                            fontSize: 'var(--font-size-14)',
                            color: getStatusColor(item.status ?? '')
                          }}>
                            {getStatusLabel(item.status ?? '')}
                          </span>
                          <span style={{ 
                            fontSize: 'var(--font-size-12)', 
                            color: 'var(--color-darkgray)' 
                          }}>
                            {formatDate(item.createdAt ?? item.updatedAt ?? '')}
                          </span>
                        </div>
                        
                        <div className="d-flex align-center gap-2 mb-1">
                          <User2 size={12} color="var(--color-darkgray)" />
                          <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                            Por: <strong>{item.updatedBy}</strong>
                            {item.userInfo?.email && (
                              <span> ({item.userInfo.email})</span>
                            )}
                          </span>
                        </div>
                        
                        {item.previousStatus && (
                          <div style={{ 
                            fontSize: 'var(--font-size-12)', 
                            color: 'var(--color-darkgray)',
                            fontStyle: 'italic'
                          }}>
                            De: {getStatusLabel(item.previousStatus)} → {getStatusLabel(item.status ?? '')}
                          </div>
                        )}
                        
                        {item.reason && (
                          <div style={{ 
                            fontSize: 'var(--font-size-12)', 
                            color: 'var(--color-darkgray)',
                            marginTop: '4px',
                            padding: '6px 8px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            border: '1px solid var(--color-gainsboro-100)'
                          }}>
                            <strong>Motivo:</strong> {item.reason}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center" style={{ padding: '24px', color: 'var(--color-darkgray)' }}>
                <History size={32} style={{ marginBottom: '12px', opacity: 0.3 }} />
                <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>
                  Nenhum histórico disponível
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showErrorAlert && (
        <PermissionAlert 
          message={errorMessage} 
          onClose={() => setShowErrorAlert(false)} 
        />
      )}
    </div>
  );
};

export default EmissionDetail;
